import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSelectedLocale} from '@ui/i18n/selected-locale';

interface BuiltinPage {
  slug: string;
  translations?: Record<string, string>;
}

declare module '@ui/bootstrap-data/bootstrap-data' {
  interface BootstrapData {
    // see BaseBootstrapData::setBuiltinPages — keyed by CustomPage.type
    builtinPages?: Record<string, BuiltinPage>;
  }
}

/**
 * Resolve a public route name (currently 'contact' or 'pricing', i.e. one
 * of the built-in CustomPage types) to its locale-prefixed URL for the
 * currently active locale, e.g.
 *   useLocalizedUrl('pricing') === '/de/preise'  (active locale = de)
 *   useLocalizedUrl('contact') === '/en/contact' (active locale = en)
 *
 * Falls back to the legacy un-prefixed path (/pricing, /contact) when the
 * built-in page isn't seeded yet — that path's backend route 301-redirects
 * to the canonical localized URL, so navigation still works.
 */
export function useLocalizedUrl(name: 'contact' | 'pricing'): string {
  const {localeCode} = useSelectedLocale();
  return resolveLocalizedUrl(name, localeCode);
}

/**
 * Non-hook variant for places that aren't React (e.g. toast actions,
 * navigation queued outside the render tree).
 */
export function getLocalizedUrl(name: 'contact' | 'pricing'): string {
  const locale = getBootstrapData()?.i18n?.active || 'en';
  return resolveLocalizedUrl(name, locale);
}

function resolveLocalizedUrl(
  name: 'contact' | 'pricing',
  locale: string,
): string {
  const page = getBootstrapData()?.builtinPages?.[name];
  if (!page) {
    return `/${name}`;
  }
  const slug = page.translations?.[locale] || page.slug;
  return `/${locale}/${slug}`;
}

/**
 * Rewrite a path from one locale's URL shape to another's. Used by the
 * locale switcher: when the user is on `/de/kontakt` and switches to
 * English, we want the address bar to flip to `/en/contact` (the canonical
 * English URL for the same page) rather than staying on `/de/kontakt`.
 *
 * The lookup walks `customPageSlugs` + `builtinPages` to find the base
 * slug behind the current URL's localized slug, then emits
 * `/{newLocale}/{slug-in-new-locale-or-base}`. URLs without a known locale
 * prefix (e.g. /drive, /admin/...) are returned unchanged.
 */
export function rewriteUrlForLocale(
  pathname: string,
  newLocale: string,
): string {
  const data = getBootstrapData();
  const configuredLocales = (data?.i18n?.locales ?? []).map(l => l.language);

  const match = pathname.match(/^\/([a-z]{2})(\/.*)?$/);
  if (!match || !configuredLocales.includes(match[1])) {
    // not a locale-prefixed URL — leave it alone (covers /drive, /admin/...)
    return pathname;
  }

  const rest = match[2] ?? '';
  // bare /{locale} → /{newLocale}
  if (!rest || rest === '/') {
    return `/${newLocale}`;
  }

  // /{oldLocale}/{slug}[/...trailing] — only rewrite the first slug segment
  const restMatch = rest.match(/^\/([^/]+)(\/.*)?$/);
  if (!restMatch) {
    return `/${newLocale}${rest}`;
  }
  const currentSlug = restMatch[1];
  const trailing = restMatch[2] ?? '';

  // 1. Built-in page? look up by current slug across base + every locale's
  //    translation slug, then resolve the new locale's slug.
  for (const builtin of Object.values(data?.builtinPages ?? {})) {
    const matchesBase = builtin.slug === currentSlug;
    const matchesTranslation = Object.values(builtin.translations ?? {}).some(
      s => s === currentSlug,
    );
    if (matchesBase || matchesTranslation) {
      const newSlug = builtin.translations?.[newLocale] || builtin.slug;
      return `/${newLocale}/${newSlug}${trailing}`;
    }
  }

  // 2. Custom page with translations: find the base slug whose translation
  //    map contains the current slug (or whose base slug *is* the current
  //    slug); emit the new locale's slug from that map.
  for (const [baseSlug, translations] of Object.entries(
    data?.customPageSlugs ?? {},
  )) {
    const isBase = baseSlug === currentSlug;
    const matchedLocale = Object.entries(translations).find(
      ([, s]) => s === currentSlug,
    )?.[0];
    if (isBase || matchedLocale) {
      const newSlug = translations[newLocale] || baseSlug;
      return `/${newLocale}/${newSlug}${trailing}`;
    }
  }

  // 3. Untranslated slug — just swap the locale segment.
  return `/${newLocale}/${currentSlug}${trailing}`;
}
